import type { Dispatch, SetStateAction } from "react";
import type {
  AdvisorMovement,
  AppNotification,
  AppData,
  ApprovalStatus,
  Client,
  DeletableModule,
  Distributor,
  Encargo,
  EncargoDeliverMode,
  EncargoStatus,
  Expense,
  ExpenseCategory,
  FundKey,
  InventoryAdjustment,
  InventoryItem,
  Loan,
  Period,
  Product,
  Promotion,
  Receivable,
  RestockItem,
  Role,
  Sale,
  Settings,
  Wallet,
} from "@/types";
import type { ProfileContact } from "@/store/api";
import type { CatalogImportResult, CatalogImportRow } from "@/utils/pdfCatalog";
import type { BackupKey, BackupPayload, RestoreSummary } from "@/utils/jsonBackup";

export const PERIOD_KEY = "attd_period";
export const ROLE_KEY = "attd_role_view";

// Entrada para mover mercancía hacia/desde un asesor (consignación, venta o devolución).
export interface AdvisorDeliveryInput {
  advisorId: string;
  advisorName: string;
  productId: string;
  productName: string;
  qty: number;
  mode: "consignacion" | "compra" | "devolucion";
  from: "casa" | "clear";
  unitValue: number;
}

// Compra del admin para ingresar a stock de Oficina. Una sola acción deja la
// compra registrada en Contabilidad y suma las unidades al inventario propio.
export interface PurchaseToStockInput {
  productId: string;
  qty: number;
  unitCost: number; // costo base por unidad (por defecto, el del catálogo)
  category: ExpenseCategory;
  wallet: Wallet;
  payment: "pendiente" | "caja" | "prestamo"; // origen de pago
}

// F6 — Reposición completa: en una sola operación compra lo que falte (ingresándolo
// a Oficina) y deja la mercancía en consignación al asesor.
export interface RestockToAdvisorInput {
  advisorId: string;
  advisorName: string;
  productId: string;
  productName: string;
  qty: number;
  category: ExpenseCategory;
  wallet: Wallet;
  payment: "pendiente" | "caja" | "prestamo";
}

// Entrega de un pedido por encargo: el admin elige si el asesor lo compra (pagado,
// va solo a su inventario propio) o queda en consignación (va a su consignación y
// sigue contando como inventario del admin). El producto debe estar primero en
// stock de Oficina (la compra se registra con "Comprar para stock").
export interface EncargoDeliveryInput {
  encargoId: string;
  mode: EncargoDeliverMode;
}

// Traspaso de unidades entre dos productos del inventario de Oficina (ej. reubicar
// mercancía heredada). No genera compra, venta ni movimiento contable: solo mueve
// stock y deja constancia (con motivo) en el historial de ajustes.
export interface TransferStockInput {
  sourceId: string;
  sourceName: string;
  targetId: string;
  targetName: string;
  qty: number;
  reason: string;
  userName: string;
  userId?: string;
}

// Contexto compartido que reciben los hooks de acciones del store.
export interface StoreCtx {
  data: AppData;
  setData: Dispatch<SetStateAction<AppData>>;
  setError: Dispatch<SetStateAction<string | null>>;
  run: (action: () => Promise<void>) => Promise<void>;
  refresh: () => Promise<void>;
  user?: { id: string } | null;
  refreshProfile?: () => Promise<void>;
}

export interface AppContextValue {
  data: AppData;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  setRole: (role: Role) => void;
  setPeriod: (period: Period) => void;
  upsertProduct: (product: Product) => Promise<void>;
  deleteProduct: (id: string) => Promise<void>;
  addDistributor: (d: Distributor) => void;
  updateDistributor: (d: Distributor) => Promise<void>;
  setApprovalStatus: (id: string, status: ApprovalStatus) => Promise<void>;
  deleteDistributor: (id: string) => Promise<void>;
  addClient: (c: Client) => Promise<Client | null>;
  updateClient: (c: Client) => Promise<void>;
  deleteClient: (id: string) => Promise<void>;
  deleteClients: (ids: string[]) => Promise<void>;
  addSale: (s: Sale) => Promise<Sale | null>;
  setInventory: (item: InventoryItem) => Promise<void>;
  applyInventoryAdjustment: (item: InventoryItem, adjustment: InventoryAdjustment) => Promise<void>;
  transferInventoryStock: (input: TransferStockInput) => Promise<void>;
  addAdvisorMovement: (m: AdvisorMovement) => Promise<void>;
  deleteAdvisorMovement: (id: string) => Promise<void>;
  deliverToAdvisor: (input: AdvisorDeliveryInput) => Promise<void>;
  addPurchaseToStock: (input: PurchaseToStockInput) => Promise<void>;
  restockToAdvisor: (input: RestockToAdvisorInput) => Promise<void>;
  registerAdvisorSale: (p: import("@/store/api").AdvisorSalePayload) => Promise<boolean>;
  addExpense: (e: Expense) => Promise<void>;
  updateExpense: (e: Expense) => Promise<void>;
  deleteExpense: (id: string) => Promise<void>;
  addFundTransfer: (input: {
    fromFund: FundKey;
    toFund: FundKey;
    amount: number;
    note: string;
  }) => Promise<void>;
  removeFundMovement: (id: string) => Promise<void>;
  payExpenseFromFund: (expenseId: string, fund: FundKey, amount: number) => Promise<void>;
  payExpenseWithLoan: (expenseId: string, amount: number) => Promise<void>;
  addReceivable: (r: Receivable) => Promise<void>;
  updateReceivable: (r: Receivable) => Promise<void>;
  removeReceivable: (id: string) => Promise<void>;
  addLoan: (l: Loan) => Promise<void>;
  removeLoan: (id: string) => Promise<void>;
  addPromotion: (p: Promotion) => Promise<void>;
  deletePromotion: (id: string) => Promise<void>;
  addEncargo: (e: Encargo) => Promise<void>;
  setEncargoStatus: (id: string, status: EncargoStatus) => Promise<void>;
  deliverEncargo: (input: EncargoDeliveryInput) => Promise<void>;
  deleteEncargo: (id: string) => Promise<void>;
  addRestockItem: (i: RestockItem) => Promise<void>;
  deleteRestockItem: (id: string) => Promise<void>;
  pushNotification: (n: Omit<AppNotification, "id" | "date" | "read">) => Promise<void>;
  markNotificationRead: (id: string) => Promise<void>;
  markAllRead: (audience: "admin" | "asesor") => Promise<void>;
  updateSettings: (patch: Partial<Settings>) => Promise<void>;
  updateProfileContact: (id: string, contact: ProfileContact) => Promise<boolean>;
  importCatalogProducts: (rows: CatalogImportRow[]) => Promise<CatalogImportResult | null>;
  loadDemo: () => Promise<void>;
  clearAll: () => Promise<void>;
  deleteMany: (module: DeletableModule, ids: string[]) => Promise<number>;
  exportBackup: () => BackupPayload;
  restoreBackup: (
    payload: BackupPayload,
    selection: BackupKey[]
  ) => Promise<RestoreSummary | null>;
}